import { environment } from '../../../../environments/environment';
import { FileService } from '../../../shared/services/file.service';
import { CandidateStore } from '../../../dashboards/store/candidate-store';
import {
  ExperienceAttachment,
  PreviewAttachment,
} from '../../../shared/models/experience-attachment';
import { AttachmentTypes } from '../../../shared/enums/attachment-types';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { AppModalComponent } from '../../../shared/components/app-modal/app-modal.component';
import { ModalConfig } from '../../../shared/models/modal.config';
import {
  Component,
  OnInit,
  ViewChild,
  Input,
  OnDestroy,
  ElementRef,
  EventEmitter,
} from '@angular/core';
import { ModalConfigHelper } from '../../../shared/helpers/modal-config-helper';
import { UploadStatus } from '../../../shared/models/upload-status';
import { FileHelper } from '../../../shared/helpers/file-helper';
import { HttpEventType } from '@angular/common/http';
import { RegexPatternHelper } from 'src/app/shared/helpers/regex-pattern-helper';
import { RegexStrings } from 'src/app/shared/constants/regex-strings';
import { FileTypes } from 'src/app/shared/enums/file-types';
import { StaticFilePaths } from 'src/app/shared/constants/static-file-paths';
import { ComponentTypes } from 'src/app/shared/enums/component-type';

@Component({
  selector: 'app-experience-attachment',
  templateUrl: './experience-attachment.component.html',
  styleUrls: ['./experience-attachment.component.scss'],
})
export class ExperienceAttachmentComponent implements OnInit, OnDestroy {
  @Input() resetAttachmentFormEvent: EventEmitter<void>;
  @ViewChild('attachmentmodal') private modalComponent: AppModalComponent;
  @Input() experienceId: string;
  modalConfig: ModalConfig;
  attachmentTypes = AttachmentTypes;
  fileTypes = FileTypes;
  componentTypes = ComponentTypes;

  experienceAttachment: ExperienceAttachment;
  acceptedAttachmentFiles: string;
  formData: FormData;
  uploadStatus: UploadStatus;
  formValidationStatus: string = '';
  subscription: Subscription;
  currentAttachment: PreviewAttachment;
  showAttachmentInDialog = false;

  constructor(
    private candidateStore: CandidateStore,
    private fileService: FileService
  ) {
    this.modalConfig = ModalConfigHelper.getAtachmentModalConfig(
      this.onDismiss,
      this.onSave
    );
    this.resetForm();
    this.acceptedAttachmentFiles = FileHelper.getAllowedAttachments();
  }

  ngOnInit() {
    this.resetAttachmentFormEvent.subscribe(() => {
      this.resetForm();
    })
  }

  handleAddMediaClick = () => {
    this.experienceAttachment.experienceId = this.experienceId;
    this.modalComponent.open();
  };

  onSave = async (): Promise<boolean> => {
    if (!this.isValidData()) {
      this.formValidationStatus =
        'Title and Attachment File or URL is required.';

      return firstValueFrom(of(false));
    }

    this.showAttachmentInDialog = false;
    this.formValidationStatus = '';

    this.experienceAttachment.experienceId = this.experienceId;
    this.candidateStore.addExperienceAttachment(this.experienceAttachment);
    this.candidateStore.getExperiences();

    return firstValueFrom(of(true));
  };

  handleFileUpload = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    this.formValidationStatus = '';
    this.experienceAttachment.attachmentType = this.attachmentTypes.File;

    return this.processFileToServer(files);
  };

  processFileToServer = (files: File[]) => {
    const fileToUpload = files[0];

    if (fileToUpload.size > (25 * 1024 * 1024)) {
      alert("File exceeded the maximum allowed size. Please upload a smaller file.");
      return;
    }

    this.formData = new FormData();
    this.formData.append('acceptedFiles', this.acceptedAttachmentFiles);
    this.formData.append('file', fileToUpload, fileToUpload.name);

    this.fileService.uploadFile(this.formData).subscribe((event) => {
      if (event.type === HttpEventType.UploadProgress) {
        this.uploadStatus.progress =
          event.loaded == event.total
            ? 99
            : Math.round((100 * event.loaded) / event.total);
      } else if (event.type === HttpEventType.Response) {
        this.experienceAttachment.filePath = event.body['dbPath'];

        if (!this.experienceAttachment.filePath) {
          this.uploadStatus.status = 'Unable to upload the file.';
          this.uploadStatus.success = false;
          return false;
        }

        // this.candidateStore.addExperienceAttachment(
        //   this.experienceAttachment
        // );

        this.imagePath();

        this.uploadStatus.success = true;
        this.uploadStatus.progress = 0;
        this.showAttachmentInDialog = true;
      }
    });
  };

  imagePath(): void {
    this.currentAttachment = this.experienceAttachment as PreviewAttachment;

    if (this.currentAttachment.attachmentType == this.attachmentTypes.URL) {
      this.currentAttachment.isYoutubeVideoLink =
        RegexPatternHelper.isValidPattern(
          this.currentAttachment.url,
          RegexStrings.YOUTUBE_VIDEO_LINK
        );

      this.currentAttachment.isAudioLink =
        RegexPatternHelper.isValidPattern(
          this.currentAttachment.url,
          RegexStrings.AUDIO_LINK
        );

      this.currentAttachment.attachmentUIPath = this.currentAttachment
        .isYoutubeVideoLink || this.currentAttachment.isAudioLink
        ? `${this.currentAttachment.url}`
        : `/${StaticFilePaths.PreviewImage}`;
    } else {
      this.currentAttachment.fileType = FileHelper.getFileType(
        this.currentAttachment.filePath
      );
      this.currentAttachment.attachmentUIPath = `${environment.baseUrl}/${this.currentAttachment.filePath}`;
    }
  }

  isValidData = (): boolean => {
    const data = this.experienceAttachment;
    return !!
      ((data.attachmentType == this.attachmentTypes.URL && data.url) ||
        (data.attachmentType == this.attachmentTypes.File &&
          this.formData &&
          this.formData.has('file')));
  };

  onDismiss = (): Observable<boolean> => {
    return of<boolean>(true);
  };

  private resetForm(): void {
    this.experienceAttachment = new ExperienceAttachment();
    this.currentAttachment = new PreviewAttachment();
    this.formData = new FormData();
    this.uploadStatus = new UploadStatus();
    this.uploadStatus.progress = 0;
    this.formValidationStatus = '';
  }

  ngOnDestroy = () => {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  };
}
